
import { 
  SparcConfig,
  analyzeCode,
  modifyCode,
  executeCode,
  searchCode,
  createCheckpoint,
  rollbackToCheckpoint,
  configureSparcAgent,
  CodeAnalysisResult,
  CodeModificationResult,
  CodeExecutionResult,
  CodeSearchResult,
} from '@/services/agentic/sparcService';

type SparcState = {
  setLoading: (loading: boolean) => void;
  setError: (error: Error | null) => void;
  setAnalyzing: (analyzing: boolean) => void;
  setModifying: (modifying: boolean) => void;
  setExecuting: (executing: boolean) => void;
  setSearching: (searching: boolean) => void;
  setAnalysisResult: (result: CodeAnalysisResult) => void;
  setModificationResult: (result: CodeModificationResult) => void;
  setExecutionResult: (result: CodeExecutionResult) => void;
  setSearchResult: (result: CodeSearchResult) => void;
  config: SparcConfig;
  setConfig: (config: SparcConfig) => void;
};

/**
 * Hook for handling SPARC agent operations
 */
export const useSparcOperations = (state: SparcState) => {
  const { 
    setLoading, 
    setError, 
    setAnalyzing, 
    setModifying, 
    setExecuting, 
    setSearching,
    setAnalysisResult,
    setModificationResult,
    setExecutionResult,
    setSearchResult,
    config,
    setConfig
  } = state;
  
  // Analyze code
  const analyze = async (files: string[], task?: string) => {
    setAnalyzing(true);
    setLoading(true);
    setError(null);
    
    try {
      const result = await analyzeCode(files, task, config);
      setAnalysisResult(result);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error during analysis');
      setError(error);
      throw error;
    } finally {
      setAnalyzing(false);
      setLoading(false);
    }
  };
  
  // Modify code
  const modify = async (files: string[], task: string) => {
    setModifying(true);
    setLoading(true);
    setError(null);
    
    try {
      const result = await modifyCode(files, task, config);
      setModificationResult(result);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error during modification');
      setError(error);
      throw error;
    } finally {
      setModifying(false);
      setLoading(false);
    }
  };
  
  // Execute code
  const execute = async (code: string, language: string) => {
    setExecuting(true);
    setLoading(true);
    setError(null);
    
    try {
      const result = await executeCode(code, language, config);
      setExecutionResult(result);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error during execution');
      setError(error);
      throw error;
    } finally {
      setExecuting(false);
      setLoading(false);
    }
  };
  
  // Search code
  const search = async (query: string, limit?: number) => {
    setSearching(true);
    setLoading(true);
    setError(null);
    
    try {
      const result = await searchCode(query, limit, config);
      setSearchResult(result);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error during search');
      setError(error);
      throw error;
    } finally {
      setSearching(false);
      setLoading(false);
    }
  };
  
  // Create checkpoint
  const checkpoint = async (name: string) => {
    setLoading(true);
    setError(null);
    
    try {
      return await createCheckpoint(name);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error creating checkpoint');
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  // Rollback to checkpoint
  const rollback = async (commit: string) => {
    setLoading(true);
    setError(null);
    
    try {
      return await rollbackToCheckpoint(commit);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error during rollback');
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  // Configure agent
  const configure = async (action: 'get' | 'set' | 'list', key?: string, value?: any) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await configureSparcAgent(action, key, value);
      // If setting a config value, update local config too
      if (action === 'set' && key) {
        setConfig({
          ...config,
          [key]: value
        });
      }
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error during configuration');
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    analyze,
    modify,
    execute,
    search,
    checkpoint,
    rollback,
    configure
  };
};
